import React, { FC, useEffect, useRef, useState } from 'react';
import { btnRow, chipStyle, secondaryBtn } from 'tools/toolStyles';
import { clampAudioTime, getLoopRestartTime, normalizeAudioSegment } from './audioLoop';

type Props = {
  src: string;
};

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5];

function formatTime(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '0:00.0';
  const minutes = Math.floor(value / 60);
  const seconds = (value % 60).toFixed(1).padStart(4, '0');
  return `${minutes}:${seconds}`;
}

const timeline: any = {
  width: '100%',
  accentColor: '#1565c0',
};

const markerText: any = {
  margin: '8px 0 12px',
  fontSize: 13,
  color: '#455a64',
};

const DictationAudioPlayer: FC<Props> = ({ src }) => {
  const audioRef = useRef<any>(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [start, setStart] = useState<number | null>(null);
  const [end, setEnd] = useState<number | null>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audio.playbackRate = 1;
    }
    setDuration(0);
    setCurrentTime(0);
    setPlaying(false);
    setSpeed(1);
    setStart(null);
    setEnd(null);
  }, [src]);

  const play = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const segment = normalizeAudioSegment(start, end, duration);
    if (segment && (audio.currentTime < segment.start || audio.currentTime >= segment.end)) {
      audio.currentTime = segment.start;
    }
    const promise = audio.play();
    if (promise && promise.catch) promise.catch(() => setPlaying(false));
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) play();
    else audio.pause();
  };

  const seek = (value: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = clampAudioTime(value, duration);
    audio.currentTime = next;
    setCurrentTime(next);
  };

  const markStart = () => {
    setStart(currentTime);
    if (end != null && end <= currentTime) setEnd(null);
  };

  const markEnd = () => {
    const proposedStart = start == null ? 0 : start;
    if (currentTime > proposedStart) {
      setStart(proposedStart);
      setEnd(currentTime);
    }
  };

  const changeSpeed = (next: number) => {
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const onTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const restart = getLoopRestartTime(audio.currentTime, start, end, duration);
    if (restart != null) {
      audio.currentTime = restart;
      setCurrentTime(restart);
      if (!audio.paused) {
        const promise = audio.play();
        if (promise && promise.catch) promise.catch(() => undefined);
      }
      return;
    }
    setCurrentTime(audio.currentTime || 0);
  };

  useEffect(() => {
    const onKeyDown = (event: any) => {
      if ((event.ctrlKey || event.metaKey) && event.code === 'Space') {
        event.preventDefault();
        togglePlay();
        return;
      }
      if (!event.altKey) return;
      if (event.key.toLowerCase() === 'a') {
        event.preventDefault();
        markStart();
      } else if (event.key.toLowerCase() === 'b') {
        event.preventDefault();
        markEnd();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        seek(currentTime - 2);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        seek(currentTime + 2);
      }
    };
    const g: any = typeof global !== 'undefined' ? global : {};
    const win = g.window || g;
    if (win.addEventListener) win.addEventListener('keydown', onKeyDown);
    return () => {
      if (win.removeEventListener) win.removeEventListener('keydown', onKeyDown);
    };
  });

  const segment = normalizeAudioSegment(start, end, duration);

  return (
    <div>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={() => {
          const audio = audioRef.current;
          if (!audio) return;
          setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
          audio.playbackRate = speed;
        }}
        onTimeUpdate={onTimeUpdate}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />

      <input
        aria-label="Vị trí audio"
        style={timeline}
        type="range"
        min={0}
        max={duration || 0}
        step={0.05}
        value={Math.min(currentTime, duration || 0)}
        onChange={(event: any) => seek(Number(event.target.value))}
      />
      <p style={markerText}>
        {formatTime(currentTime)} / {formatTime(duration)} · A {start == null ? '—' : formatTime(start)} · B{' '}
        {end == null ? '—' : formatTime(end)} {segment ? '· đang lặp A–B' : ''}
      </p>

      <div style={btnRow}>
        <button type="button" style={secondaryBtn} onClick={togglePlay}>
          {playing ? 'Tạm dừng' : 'Phát'}
        </button>
        <button type="button" style={secondaryBtn} onClick={() => seek(currentTime - 2)}>
          −2 giây
        </button>
        <button type="button" style={secondaryBtn} onClick={() => seek(currentTime + 2)}>
          +2 giây
        </button>
        <button type="button" style={secondaryBtn} onClick={markStart}>
          Đặt A
        </button>
        <button type="button" style={secondaryBtn} onClick={markEnd}>
          Đặt B
        </button>
        <button
          type="button"
          style={secondaryBtn}
          onClick={() => {
            setStart(null);
            setEnd(null);
          }}
        >
          Xóa A–B
        </button>
      </div>

      <div style={btnRow} aria-label="Tốc độ phát">
        {SPEEDS.map((item) => (
          <button key={item} type="button" style={chipStyle(speed === item)} onClick={() => changeSpeed(item)}>
            {item === 1 ? '1.0' : String(item)}×
          </button>
        ))}
      </div>
      <p style={{ ...markerText, marginBottom: 0 }}>
        Phím tắt: Ctrl/⌘ + Space phát/dừng, Alt+A/B đặt đoạn, Alt+←/→ tua 2 giây.
      </p>
    </div>
  );
};

export default DictationAudioPlayer;
